<script lang="ts" setup>
import AppButton from "@/components/atoms/button/AppButton.vue";
import { defineProps, defineEmits, PropType, computed } from "vue";
import { User } from "@/types/user/User";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { Application } from "@/types/order/Application";
import { orderService } from "@/app/services/OrderService";
import { applicationService } from "@/app/services/ApplicationService";
import { authStore } from "@/store/auth";

const props = defineProps({
  user: {
    type: Object as PropType<User>,
    required: true
  },
  applicationToRemind: {
    type: Object as PropType<Application>,
    required: true
  }
});
const emit = defineEmits(["cancel", "apply"]);

const mainVisualImage = computed(() =>
  require("@/assets/img/applicationReminder/MainVisual" +
    authStore.productAssetSuffix +
    ".png")
);

const getFormattedDate = (application: Application): string => {
  return orderService.getFormattedDateWithSlash(application.order);
};

const getFormattedWorkTimeRange = (application: Application): string => {
  return applicationService.getFormattedWorkTimeRange(application);
};

const onCancel = () => {
  emit("cancel");
};

const onApply = () => {
  emit("apply");
};
</script>

<template>
  <div class="container">
    <img
      :src="mainVisualImage"
      alt="明日の勤務でのご活躍を応援しています"
      class="main-visual"
    />
    <div>
      <p class="header-text">前日確認です</p>
      <p>予定通り勤務できますか？</p>
      <p>{{ user.lastName }}さんを頼りにしている方がいます</p>
    </div>
    <div class="balloon">
      <p class="mb-2">
        <FeatherIcon icon="calendar" size="20" class="date-icon" /><span
          class="ml-1 mr-4 strong-text"
          >{{ getFormattedDate(applicationToRemind) }}</span
        >
        <FeatherIcon icon="clock" size="20" class="date-icon" /><span
          class="ml-1 strong-text"
          >{{ getFormattedWorkTimeRange(applicationToRemind) }}</span
        >
      </p>
      <p>
        {{ applicationToRemind.order.office.name }}
      </p>
    </div>
    <AppButton class="mt-2 mb-3 text-size-20" @click="onApply">
      予定通り勤務できる
    </AppButton>
    <div class="cancel-container">
      <div class="cancel-warning">
        <div class="cancel-warning-header">
          <span class="warning-icon">!</span>当日キャンセルはご遠慮ください
        </div>
        <div>当日キャンセルをした場合、</div>
        <ol class="cancel-warning-list">
          <li>
            現場が混乱し、<span class="cancel-warning-bold">負担増</span>
            につながります
          </li>
          <li>
            信用スコアが下がり、一時的に
            <span class="cancel-warning-bold">利用停止</span>
            になる可能性があります
          </li>
          <li>
            直前キャンセルが多い方は、事業所から
            <span class="cancel-warning-bold">応募後にお断りされる</span>
            場合があります
          </li>
        </ol>
      </div>
      <div class="cancel-guide">
        <div>
          少しでも体調に不安があれば、<br />
          前日までにご調整をよろしくお願いします
        </div>
        <div>
          <button class="btn-link cancel-text" @click="onCancel">
            前日キャンセルはこちら
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0;
}

.main-visual {
  width: 100%;
}

.header-text {
  font-size: 18px;
  font-weight: 600;
}

.balloon {
  position: relative;
  padding: 12px 16px;
  border: 2px solid #e5e5e5;
  border-radius: 12px;

  .date-icon {
    vertical-align: sub;
  }
}

.balloon::before {
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -15px;
  content: "";
  border: 15px solid transparent;
  border-top: 15px solid #e5e5e5;
}

.balloon::after {
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -13px;
  content: "";
  border: 13px solid transparent;
  border-top: 13px solid #fff;
}

.strong-text {
  font-weight: 600;
}

.cancel-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.cancel-warning {
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: #f00;
}

.warning-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 16px;
  padding-top: 1px;
  clip-path: polygon(50% 0%, 100% 100%, 0% 100%);
  font-size: 10px;
  font-weight: bold;
  line-height: 1;
  color: #fff;
  background-color: red;
}

.cancel-warning-header {
  display: flex;
  gap: 2px;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
}

.cancel-warning-bold {
  font-weight: 600;
  color: #f00;
}

.cancel-warning-list {
  padding-left: 1em;
  text-indent: -1em;
  list-style-position: inside;
  list-style-type: decimal;
}

.cancel-guide {
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 14px;
  line-height: 14px;
  color: #6b6b6b;
  text-align: center;
}

.cancel-text {
  color: #6b6b6b;
  text-decoration: underline;
}
</style>
